import { useMemo, useRef } from 'react';

import useLocalStorage from 'hooks/useLocalStorage';

import { cuestionariosApi } from '../api/cuestionarios-api';
import { NIVEL_DD } from 'types/nivel-dd';
import type {
  CuestionarioBuilderData,
  CuestionarioBuilderDomain,
  CuestionarioBuilderQuestion,
  CuestionarioApiPaginated,
  CuestionarioApiIndexItem,
  CuestionarioNuevoDraft,
  CuestionarioResumen,
  CuestionarioOwner,
} from '../types';

type StoreState = {
  cuestionarios: CuestionarioResumen[];
  builders: Record<string, CuestionarioBuilderData>;
};

const storeKey = 'dd_cuestionarios_store_v2';
const wizardKey = 'dd_cuestionarios_wizard_v2';

const defaultStore: StoreState = {
  cuestionarios: [],
  builders: {}
};

const defaultWizardDraft: CuestionarioNuevoDraft = {
  info: {
    nombre: '',
    idiomas: ['ES', 'EN', 'PT'],
    idiomaPredeterminado: 'ES',
    descripcion: '',
    grupoIds: [],
    tipoCuestionarioId: null,
    tipoFigura: 'juridica',
    nivelDd: NIVEL_DD.ESTANDAR,
    regionId: null,
    sectorId: null
  },
  responsables: {
    miembros: []
  },
  plantilla: { plantillaId: null, search: '', mode: 'plantilla' }
};

function randomId(prefix: string) {
  const n = Math.random().toString(16).slice(2);
  return `${prefix}-${n}`;
}

export function toInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '');
  return letters.join('') || name.slice(0, 2).toUpperCase();
}

function toAcronym(name: string) {
  const ignore = new Set(['y', 'de', 'del', 'la', 'las', 'el', 'los', 'en', 'a', 'al']);
  const parts = name
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .split(/\s+/)
    .filter(Boolean)
    .filter((p) => !ignore.has(p.toLowerCase()));

  const letters = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
  return letters.slice(0, 6) || name.slice(0, 4).toUpperCase();
}

function domainTotalPond(domainId: string, allDomains: CuestionarioBuilderDomain[]): number {
  const domain = allDomains.find((d) => d.id === domainId);
  if (!domain) return 0;
  const own = domain.preguntas.reduce((acc, q) => acc + (Number.isFinite(q.ponderacion) ? q.ponderacion : 0), 0);
  const childSum = allDomains
    .filter((d) => d.parentId === domainId)
    .reduce((acc, child) => acc + domainTotalPond(child.id, allDomains), 0);
  return own + childSum;
}

function mapIdiomaNombre(nombre: string): 'ES' | 'PT' | 'EN' {
  const n = nombre.toLowerCase();
  if (n.includes('españ') || n.includes('spanish')) return 'ES';
  if (n.includes('portu') || n.includes('portugu')) return 'PT';
  return 'EN';
}

export function useCuestionariosStore() {
  const [store, setStore] = useLocalStorage<StoreState>(storeKey, defaultStore);
  const [storedWizardDraft, setWizardDraft] = useLocalStorage<CuestionarioNuevoDraft>(wizardKey, defaultWizardDraft);
  // Los borradores guardados antes de soportar varios grupos traen `grupoId` (uno solo) en vez de `grupoIds`.
  const wizardDraft = useMemo<CuestionarioNuevoDraft>(() => {
    const info = storedWizardDraft.info as CuestionarioNuevoDraft['info'] & { grupoId?: number | null };
    if (Array.isArray(info.grupoIds)) return storedWizardDraft;
    return { ...storedWizardDraft, info: { ...info, grupoIds: info.grupoId ? [info.grupoId] : [] } };
  }, [storedWizardDraft]);

  const fetchingRef = useRef<Record<string, boolean>>({});
  const fetchedListRef = useRef<{ q: string } | null>(null);

  const builderFromConfig = (apiData: any): CuestionarioBuilderData => {
    const idiomaActivoNombre =
      apiData.cuestionario.idiomas.find((i: any) => i.id === apiData.idioma_id)?.nombre ??
      apiData.cuestionario.idiomas.find((i: any) => i.id === apiData.cuestionario.idioma_predeterminado_id)?.nombre ??
      'Español';

    const propietarios = apiData.cuestionario.propietarios.map((p: any) => ({
      id: p.id,
      nombre: p.name,
      iniciales: toInitials(p.name),
      color: 'success',
    })) as CuestionarioOwner[];

    const responsables = apiData.cuestionario.responsables.map((p: any) => ({
      id: p.id,
      nombre: p.name,
      iniciales: toInitials(p.name),
      color: 'info',
    })) as CuestionarioOwner[];

    const apiUserMap = new Map<number, CuestionarioOwner>();
    propietarios.forEach((o) => apiUserMap.set(o.id, o));
    responsables.forEach((o) => apiUserMap.set(o.id, o));

    const allApiDomains = Array.isArray(apiData.dominios) ? apiData.dominios : [];
    const domainsByParentId = new Map<number | null, any[]>();
    allApiDomains.forEach((d: any) => {
      const parentId = (d?.parent_id ?? null) as number | null;
      const list = domainsByParentId.get(parentId) ?? [];
      list.push(d);
      domainsByParentId.set(parentId, list);
    });

    domainsByParentId.forEach((list, key) => {
      domainsByParentId.set(
        key,
        [...list].sort((a: any, b: any) => Number(a?.orden ?? 0) - Number(b?.orden ?? 0))
      );
    });

    const orderedApiDomains: any[] = [];
    const visited = new Set<number>();
    const visit = (parentId: number | null) => {
      const children = domainsByParentId.get(parentId) ?? [];
      children.forEach((d: any) => {
        const id = Number(d?.id ?? 0);
        if (!id || visited.has(id)) return;
        visited.add(id);
        orderedApiDomains.push(d);
        visit(id);
      });
    };
    visit(null);
    allApiDomains.forEach((d: any) => {
      const id = Number(d?.id ?? 0);
      if (!id || visited.has(id)) return;
      visited.add(id);
      orderedApiDomains.push(d);
    });

    return {
      versionId: apiData.version.id,
      idiomaActivo: mapIdiomaNombre(idiomaActivoNombre),
      idiomas: apiData.cuestionario.idiomas.map((i: any) => mapIdiomaNombre(i.nombre)),
      nombre: apiData.version.nombre || apiData.cuestionario.nombre,
      descripcion: apiData.cuestionario.descripcion ?? null,
      estatus: apiData.version.estado,
      puedeEditar: Boolean(apiData.cuestionario.puede_editar),
      versionActual: `v${apiData.version.version_numero}`,
      grupos: (apiData.cuestionario.grupos ?? []).map((g: any) => ({ id: g.id, sigla: g.sigla ?? '', nombre: g.nombre })),
      nivelDd: apiData.cuestionario.nivel_dd?.nombre ?? '',
      tipoTercero: apiData.cuestionario.tipo_tercero?.nombre ?? '',
      tipoFigura: apiData.cuestionario.tipo_figura?.nombre ?? '',
      region: apiData.cuestionario.region_clac?.nombre ?? '',
      sector: apiData.cuestionario.sector?.nombre ?? '',
      creado: '',
      ultimaModificacion: apiData.version.terminado_en ?? '',
      propietarios,
      responsable: responsables,
      dominios: orderedApiDomains.map((d: any) => ({
        id: `d-${d.id}`,
        parentId: d.parent_id ? `d-${d.parent_id}` : null,
        nombre: d.nombre || '',
        ponderacion: d.ponderacion,
        preguntas: d.preguntas.map((q: any) => ({
          id: `q-${q.id}`,
          orden: q.orden,
          texto: q.titulo || '',
          tipoRespuesta: q.tipo_respuesta,
          obligatorio: q.obligatorio,
          ponderacion: q.ponderacion,
          informacionAdicional: q.informacion_adicional || undefined,
          notasRevisor: q.notas_revisor || undefined,
          riesgoHabilitado: q.riesgo_habilitado,
          riesgoSi: typeof q.riesgo_si === 'number' ? q.riesgo_si : 1,
          riesgoNo: typeof q.riesgo_no === 'number' ? q.riesgo_no : 1,
          solicitarAclaraciones: q.solicitar_aclaraciones,
          solicitarDocumentos: q.solicitar_documentos,
          documentosFrecuencia: q.documentos_frecuencia,
          documentosCategoria: q.documentos_categoria,
          vigenciaHabilitada: q.vigencia_habilitada,
          fechaVigencia: q.fecha_vigencia || undefined,
          logicaHabilitada: q.logica_habilitada,
          opciones: q.opciones.map((o: any) => ({
            texto: o.texto || '',
            riesgo: o.riesgo,
          })),
          condiciones: q.condiciones.map((c: any) => ({
            preguntaId: `q-${c.depende_de_pregunta_id}`,
            valor: c.valor_esperado ? 'Sí' : 'No',
          })),
          fechaMinHabilitada: Boolean(q.fecha_min_habilitada),
          fechaMin: q.fecha_min || undefined,
          fechaMaxHabilitada: Boolean(q.fecha_max_habilitada),
          fechaMax: q.fecha_max || undefined,
          archivosPermitidos: Array.isArray(q.archivos_permitidos) ? q.archivos_permitidos : [],
          archivosTamanoMaxMb: typeof q.archivos_tamano_max_mb === 'number' ? q.archivos_tamano_max_mb : 5,
        })) as CuestionarioBuilderQuestion[],
      })) as CuestionarioBuilderDomain[],
      comentarios: mapComentarios(apiData),
    };
  };

  const mapComentarios = (apiData: any): CuestionarioBuilderData['comentarios'] => {
    const propietarios = apiData.cuestionario.propietarios.map((p: any) => ({
      id: p.id,
      nombre: p.name,
      iniciales: toInitials(p.name),
      color: 'success',
    })) as CuestionarioOwner[];

    const responsables = apiData.cuestionario.responsables.map((p: any) => ({
      id: p.id,
      nombre: p.name,
      iniciales: toInitials(p.name),
      color: 'info',
    })) as CuestionarioOwner[];

    const apiUserMap = new Map<number, CuestionarioOwner>();
    propietarios.forEach((o: CuestionarioOwner) => apiUserMap.set(o.id, o));
    responsables.forEach((o: CuestionarioOwner) => apiUserMap.set(o.id, o));

    return apiData.comentarios.map((c: any) => {
      const nombreAutor = c.usuario?.name ?? '';
      const autorApi = c.user_id ? apiUserMap.get(c.user_id) : null;
      const autor =
        autorApi ??
        (nombreAutor
          ? ({ id: c.user_id ?? 0, nombre: nombreAutor, iniciales: toInitials(nombreAutor), color: 'neutral' } as CuestionarioOwner)
          : ({ id: 0, nombre: '', iniciales: '', color: 'neutral' } as CuestionarioOwner));

      return {
        id: `c-${c.id}`,
        autor,
        texto: c.comentario,
        fecha: c.created_at || '',
      };
    });
  };

  const resumenFromConfig = (id: string, apiData: any): CuestionarioResumen => {
    const propietarios = apiData.cuestionario.propietarios.map((p: any) => ({
      id: p.id,
      nombre: p.name,
      iniciales: toInitials(p.name),
      color: 'success',
    })) as CuestionarioOwner[];

    const version = `v${apiData.version.version_numero}`;
    const grupos = (apiData.cuestionario.grupos ?? []).map((g: any) => ({ id: g.id, sigla: g.sigla ?? toAcronym(g.nombre), nombre: g.nombre }));

    return {
      id,
      nombre: apiData.cuestionario.nombre,
      descripcion: apiData.cuestionario.descripcion,
      estatus: apiData.cuestionario.estado,
      version,
      tipoTercero: apiData.cuestionario.tipo_tercero?.nombre ?? '',
      tipoFigura: apiData.cuestionario.tipo_figura?.nombre ?? '',
      idiomas: (apiData.cuestionario.idiomas ?? []).map((i: any) => mapIdiomaNombre(i.nombre)),
      grupo: apiData.cuestionario.grupos?.[0]?.sigla ?? '',
      grupos,
      nivelDd: apiData.cuestionario.nivel_dd?.nombre ?? '',
      propietarios,
      canEdit: apiData.cuestionario.estado !== 'archivado',
    };
  };

  const resolveInfoCatalogIds = async (info: CuestionarioNuevoDraft['info']) => {
    const [idiomasRes, nivelesRes, figuraRes] = await Promise.all([
      cuestionariosApi.listCatalogosByTipo('idioma'),
      cuestionariosApi.listCatalogosByTipo('nivel_dd'),
      cuestionariosApi.listCatalogosByTipo('tipo_figura'),
    ]);

    const idiomaIdByCode = new Map<CuestionarioNuevoDraft['info']['idiomas'][number], number>();
    (idiomasRes.data ?? []).forEach((i) => {
      const code = String(i.codigo ?? '').trim().toUpperCase();
      if (code === 'ES' || code === 'EN' || code === 'PT') {
        idiomaIdByCode.set(code as CuestionarioNuevoDraft['info']['idiomas'][number], i.id);
      }
    });

    const idiomaIds = info.idiomas.map((c) => idiomaIdByCode.get(c)).filter((v): v is number => typeof v === 'number');
    const idiomaPredId = idiomaIdByCode.get(info.idiomaPredeterminado) ?? idiomaIds[0];
    if (!idiomaPredId || idiomaIds.length === 0) {
      throw new Error('Missing idiomas mapping');
    }

    const nivelIdByCodigo = new Map<string, number>();
    (nivelesRes.data ?? []).forEach((n) => {
      const codigo = String(n.codigo ?? '').trim().toLowerCase();
      if (codigo) nivelIdByCodigo.set(codigo, n.id);
    });

    const figuraIdByCodigo = new Map<CuestionarioNuevoDraft['info']['tipoFigura'], number>();
    (figuraRes.data ?? []).forEach((f) => {
      const code = String(f.codigo ?? '').trim().toLowerCase();
      if (code === 'natural' || code === 'juridica') {
        figuraIdByCodigo.set(code as CuestionarioNuevoDraft['info']['tipoFigura'], f.id);
      }
    });

    const nivelDdId = nivelIdByCodigo.get(info.nivelDd) ?? null;
    const tipoFiguraId = figuraIdByCodigo.get(info.tipoFigura) ?? null;
    if (!nivelDdId || !tipoFiguraId) {
      throw new Error('Missing nivel_dd/tipo_figura mapping');
    }

    return { idiomaIds, idiomaPredId, nivelDdId, tipoFiguraId };
  };

  const actions = useMemo(() => {
    return {
      resetWizard: () => setWizardDraft(defaultWizardDraft),

      fetchCuestionariosFromApi: async (params?: { q?: string; force?: boolean }) => {
        const q = (params?.q ?? '').trim();
        if (!params?.force && fetchedListRef.current?.q === q) return;
        fetchedListRef.current = { q };

        try {
          const response: CuestionarioApiPaginated = await cuestionariosApi.list({ q, per_page: 100, page: 1 });
          const apiItems = response.data ?? [];

          const mapped: CuestionarioResumen[] = apiItems.map((q: CuestionarioApiIndexItem) => {
            const propietarios = (q.propietarios ?? []).map((p) => ({
              id: p.id,
              nombre: p.name,
              iniciales: toInitials(p.name),
              color: 'success',
            })) as CuestionarioOwner[];

            const version = q.version_actual ? `v${q.version_actual.version_numero}` : 'v1';
            const grupos = (q.grupos ?? []).map((g) => ({
              id: g.id,
              sigla: g.sigla ?? toAcronym(g.nombre),
              nombre: g.nombre
            }));
            const nivelDd = q.nivel_dd?.nombre ?? '';

            return {
              id: q.uuid,
              nombre: q.nombre,
              descripcion: q.descripcion,
              estatus: q.estado,
              version,
              tipoTercero: q.tipo_tercero?.nombre ?? '',
              tipoFigura: q.tipo_figura?.nombre ?? '',
              idiomas: (q.idiomas ?? []).map((i) => mapIdiomaNombre(i.nombre)),
              grupo: q.grupos?.[0]?.sigla ?? '',
              grupos,
              nivelDd,
              propietarios,
              canEdit: q.estado !== 'archivado',
            };
          });

          setStore((prev: StoreState) => ({ ...prev, cuestionarios: mapped }));
        } catch (error) {
          fetchedListRef.current = null;
          console.error(error);
        }
      },

      setWizardInfo: (patch: Partial<CuestionarioNuevoDraft['info']>) =>
        setWizardDraft((prev: CuestionarioNuevoDraft) => ({ ...prev, info: { ...prev.info, ...patch } })),

      setWizardResponsables: (miembros: CuestionarioNuevoDraft['responsables']['miembros']) =>
        setWizardDraft((prev: CuestionarioNuevoDraft) => ({ ...prev, responsables: { miembros } })),

      setWizardPlantilla: (patch: Partial<CuestionarioNuevoDraft['plantilla']>) =>
        setWizardDraft((prev: CuestionarioNuevoDraft) => ({ ...prev, plantilla: { ...prev.plantilla, ...patch } })),

      archive: async (id: string) => {
        await cuestionariosApi.archive(id);
        setStore((prev: StoreState) => ({
          ...prev,
          cuestionarios: prev.cuestionarios.map((q) => (q.id === id ? { ...q, estatus: 'archivado', canEdit: false } : q))
        }));
      },

      createFromWizardApi: async (params: { currentUserId: number; mode?: 'plantilla' | 'cero' }) => {
        const mode = params.mode ?? wizardDraft.plantilla.mode ?? 'plantilla';
        const info = wizardDraft.info;
        if (!info.grupoIds?.length || !info.tipoCuestionarioId) {
          throw new Error('Missing required wizard fields');
        }

        const { idiomaIds, idiomaPredId, nivelDdId, tipoFiguraId } = await resolveInfoCatalogIds(info);

        const plantillaId = mode === 'plantilla' ? wizardDraft.plantilla.plantillaId : null;

        const created = await cuestionariosApi.create({
          nombre: info.nombre.trim(),
          descripcion: info.descripcion?.trim() || null,
          tipo_tercero_id: info.tipoCuestionarioId,
          sector_id: info.sectorId ?? null,
          idioma_predeterminado_id: idiomaPredId,
          idiomas: idiomaIds,
          grupos: info.grupoIds,
          propietarios: [params.currentUserId],
          responsables: [params.currentUserId],
          analisis_riesgo: false,
          nivel_dd_id: nivelDdId,
          tipo_figura_id: tipoFiguraId,
          region_id: info.regionId,
          plantilla_id: plantillaId,
        });

        const cuestionarioId = created.uuid;

        // El clonado de la plantilla (si aplica) ya ocurrió en el backend, dentro de la misma
        // transacción que crea el cuestionario — no hace falta un segundo request para copiarla.
        const cfg = await cuestionariosApi.getConfig(cuestionarioId);

        const builderData = builderFromConfig(cfg);
        const resumen = resumenFromConfig(cuestionarioId, cfg);

        setStore((prev: StoreState) => ({
          ...prev,
          cuestionarios: [resumen, ...prev.cuestionarios.filter((q) => q.id !== cuestionarioId)],
          builders: { ...prev.builders, [cuestionarioId]: builderData },
        }));

        return cuestionarioId;
      },

      updateCuestionarioInfoApi: async (id: string, info: CuestionarioNuevoDraft['info']) => {
        if (!info.grupoIds?.length || !info.tipoCuestionarioId) {
          throw new Error('Missing required fields');
        }

        const { idiomaIds, idiomaPredId, nivelDdId, tipoFiguraId } = await resolveInfoCatalogIds(info);

        await cuestionariosApi.update(id, {
          nombre: info.nombre.trim(),
          descripcion: info.descripcion?.trim() || null,
          tipo_tercero_id: info.tipoCuestionarioId,
          sector_id: info.sectorId ?? null,
          idioma_predeterminado_id: idiomaPredId,
          idiomas: idiomaIds,
          grupos: info.grupoIds,
          nivel_dd_id: nivelDdId,
          tipo_figura_id: tipoFiguraId,
          region_id: info.regionId,
        });

        const cfg = await cuestionariosApi.getConfig(id);
        const builderData = builderFromConfig(cfg);
        const resumen = resumenFromConfig(id, cfg);

        setStore((prev: StoreState) => ({
          ...prev,
          cuestionarios: [resumen, ...prev.cuestionarios.filter((q) => q.id !== id)],
          builders: { ...prev.builders, [id]: builderData },
        }));
      },

      getBuilder: (id: string) => store.builders[id],

      fetchBuilderFromApi: async (id: string, force = false): Promise<{ ok: boolean; status?: number }> => {
        if (fetchingRef.current[id] || (!force && store.builders[id])) return { ok: true };
        fetchingRef.current[id] = true;

        try {
          const apiData = await cuestionariosApi.getConfig(id);
          const builderData = builderFromConfig(apiData);

          setStore((prev: StoreState) => ({
            ...prev,
            builders: { ...prev.builders, [id]: builderData }
          }));

          return { ok: true };
        } catch (error) {
          console.error('Failed to load builder from API', error);
          const status = (error as { response?: { status?: number } })?.response?.status;
          return { ok: false, status };
        } finally {
          fetchingRef.current[id] = false;
        }
      },

      ensureBuilder: (id: string) => {
        if (store.builders[id]) return;
        void (async () => {
          try {
            const apiData = await cuestionariosApi.getConfig(id);
            const builderData = builderFromConfig(apiData);
            setStore((prev: StoreState) => ({
              ...prev,
              builders: { ...prev.builders, [id]: builderData }
            }));
          } catch (error) {
            console.error('Failed to ensure builder from API', error);
          }
        })();
      },

      createNewVersionFromApi: async (id: string) => {
        const cfg = await cuestionariosApi.createVersion(id);
        const builderData = builderFromConfig(cfg as any);
        const resumen = resumenFromConfig(id, cfg as any);

        setStore((prev: StoreState) => ({
          ...prev,
          cuestionarios: [resumen, ...prev.cuestionarios.filter((q) => q.id !== id)],
          builders: { ...prev.builders, [id]: builderData },
        }));

        return id;
      },

      updateBuilder: (id: string, patch: Partial<CuestionarioBuilderData>) => {
        setStore((prev: StoreState) => {
          const current = prev.builders[id];
          if (!current) return prev;
          return { ...prev, builders: { ...prev.builders, [id]: { ...current, ...patch } } };
        });
      },

      saveDraftToApi: async (id: string) => {
        const builder = store.builders[id] as CuestionarioBuilderData | undefined;
        if (!builder) return;

        const cfg = await cuestionariosApi.getConfig(id);
        const idiomaId = cfg.idioma_id;
        const idiomaActivoId = cfg.version.idioma_activo_id ?? idiomaId;

        const payload = {
          idioma_id: idiomaId,
          idioma_activo_id: idiomaActivoId,
          nombre: builder.nombre,
          dominios: builder.dominios.map((d: CuestionarioBuilderDomain, dIdx: number) => ({
            key: d.id,
            orden: dIdx + 1,
            ponderacion: domainTotalPond(d.id, builder.dominios),
            parent_key: d.parentId ?? null,
            nombre: d.nombre,
            preguntas: d.preguntas.map((q: CuestionarioBuilderQuestion, qIdx: number) => ({
              key: q.id,
              orden: qIdx + 1,
              tipo_respuesta: q.tipoRespuesta,
              obligatorio: q.obligatorio,
              riesgo_habilitado: q.riesgoHabilitado ?? false,
              riesgo_si: q.tipoRespuesta === 'si-no' ? (q.riesgoSi ?? 1) : undefined,
              riesgo_no: q.tipoRespuesta === 'si-no' ? (q.riesgoNo ?? 1) : undefined,
              ponderacion: q.ponderacion,
              titulo: q.texto,
              informacion_adicional: q.informacionAdicional,
              notas_revisor: q.notasRevisor,
              solicitar_aclaraciones: q.solicitarAclaraciones ?? false,
              solicitar_documentos: q.solicitarDocumentos ?? false,
              documentos_frecuencia: q.documentosFrecuencia ?? 'siempre',
              documentos_categoria: q.documentosCategoria ?? 'declaraciones',
              vigencia_habilitada: q.vigenciaHabilitada ?? false,
              fecha_vigencia: q.fechaVigencia || null,
              logica_habilitada: q.logicaHabilitada ?? false,
              condiciones: q.condiciones?.map((c: any, cIdx: number) => ({
                depende_de_key: c.preguntaId,
                valor_esperado: c.valor === 'Sí',
                operador: c.operador,
                orden: cIdx + 1
              })),
              opciones: q.opciones?.map((o: any, oIdx: number) => ({
                orden: oIdx + 1,
                riesgo: o.riesgo,
                texto: o.texto
              })),
              fecha_min_habilitada: q.fechaMinHabilitada ?? false,
              fecha_min: q.fechaMinHabilitada ? (q.fechaMin || null) : null,
              fecha_max_habilitada: q.fechaMaxHabilitada ?? false,
              fecha_max: q.fechaMaxHabilitada ? (q.fechaMax || null) : null,
              archivos_tamano_max_mb: q.tipoRespuesta === 'file' ? (q.archivosTamanoMaxMb ?? 5) : undefined,
              archivos_permitidos: q.tipoRespuesta === 'file' ? (q.archivosPermitidos ?? []) : undefined,
            }))
          }))
        };

        await cuestionariosApi.upsertDraft(id, payload as any);
      },

      updateCuestionario: (id: string, patch: Partial<CuestionarioResumen>) => {
        setStore((prev: StoreState) => ({
          ...prev,
          cuestionarios: prev.cuestionarios.map((q) => (q.id === id ? { ...q, ...patch } : q))
        }));
      },

      updateBuilderDomains: (id: string, updater: (prevDomains: CuestionarioBuilderData['dominios']) => CuestionarioBuilderData['dominios']) => {
        setStore((prev: StoreState) => {
          const current = prev.builders[id];
          if (!current) return prev;
          return { ...prev, builders: { ...prev.builders, [id]: { ...current, dominios: updater(current.dominios) } } };
        });
      },

      addComment: async (id: string, author: CuestionarioOwner, texto: string, fecha: string) => {
        const trimmed = texto.trim();
        if (!trimmed) return;

        const tempId = randomId('c');
        setStore((prev: StoreState) => {
          const current = prev.builders[id];
          if (!current) return prev;
          const next = { ...current, comentarios: [{ id: tempId, autor: author, texto: trimmed, fecha }, ...current.comentarios] };
          return { ...prev, builders: { ...prev.builders, [id]: next } };
        });

        try {
          const current = store.builders[id];
          let versionId = current?.versionId;
          if (!versionId) {
            const freshCfg = await cuestionariosApi.getConfig(id);
            versionId = freshCfg.version.id;
            setStore((prev: StoreState) => {
              const prevBuilder = prev.builders[id];
              if (!prevBuilder) return prev;
              return { ...prev, builders: { ...prev.builders, [id]: { ...prevBuilder, versionId } } };
            });
          }

          const cfg = await cuestionariosApi.addComment(id, versionId, { comentario: trimmed });
          const comentarios = mapComentarios(cfg as any);
          setStore((prev: StoreState) => {
            const prevBuilder = prev.builders[id];
            if (!prevBuilder) return prev;
            return { ...prev, builders: { ...prev.builders, [id]: { ...prevBuilder, comentarios } } };
          });
        } catch (error) {
          setStore((prev: StoreState) => {
            const current = prev.builders[id];
            if (!current) return prev;
            return {
              ...prev,
              builders: {
                ...prev.builders,
                [id]: {
                  ...current,
                  comentarios: current.comentarios.filter((c) => c.id !== tempId),
                },
              },
            };
          });
          console.error('Failed to save comment to API', error);
        }
      }
    };
  }, [setStore, setWizardDraft, store.builders, wizardDraft]);

  return { store, wizardDraft, usuarios: [] as CuestionarioOwner[], actions };
}
